import { type FC, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Alert, Box, Paper, Text, UnstyledButton } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";

import { useRemoteAssetsRoutes } from "../../embed/routes/useRemoteAssetsRoutes";
import { useSelectedOrganizationContainer } from "../../shared/hooks";
import { useErrorMessage } from "../hooks/useErrorMessage";
import { useMutationStatus } from "../hooks/useMutationStatus";
import { useRemoteAsset } from "../hooks/useRemoteAsset";

import { RemoteAssetDetailForm } from "./RemoteAssetDetailForm";
import { backLink, header, headerName, page, typeBadge } from "./RemoteAssetDetailPage.module.css";
import { RemoteAssetPreview } from "./RemoteAssetPreview";
import { BackToRemoteAssets, DetailLoading, DetailNotFound } from "./messages.generated";

const isFromListState = (state: unknown): state is { fromList: boolean } =>
    typeof state === "object" && state !== null && "fromList" in state;

export const RemoteAssetDetailPage: FC = () => {
    const { remoteAssetId } = useParams<{ remoteAssetId: string }>();
    const { orgId } = useSelectedOrganizationContainer();
    const navigate = useNavigate();
    const routes = useRemoteAssetsRoutes();
    const location = useLocation();
    const cameFromList = isFromListState(location.state) && location.state.fromList;

    const { asset, lenses, hasMoreLenses, fetchMoreLenses, loadingMoreLenses, loadMoreLensesError, loading, error } =
        useRemoteAsset(remoteAssetId ?? null);
    const errorMessage = useErrorMessage(error);
    const loadMoreLensesErrorMessage = useErrorMessage(loadMoreLensesError);
    const [mutationStatus, setMutationStatus] = useMutationStatus();

    const handleBack = useCallback(() => {
        if (cameFromList) {
            navigate(-1);
        } else {
            navigate(routes.assetList(orgId ?? ""));
        }
    }, [cameFromList, navigate, orgId, routes]);

    if (loading && !asset) {
        return (
            <Box className={page}>
                <Text c="dimmed">
                    <DetailLoading />
                </Text>
            </Box>
        );
    }

    if (error && !asset) {
        console.error("Failed to load remote asset:", error);
        return (
            <Box className={page}>
                <UnstyledButton className={backLink} onClick={handleBack}>
                    <IconArrowLeft size={16} />
                    <BackToRemoteAssets />
                </UnstyledButton>
                <Text c="red">{errorMessage}</Text>
            </Box>
        );
    }

    if (!asset) {
        return (
            <Box className={page}>
                <UnstyledButton className={backLink} onClick={handleBack}>
                    <IconArrowLeft size={16} />
                    <BackToRemoteAssets />
                </UnstyledButton>
                <Text c="dimmed">
                    <DetailNotFound />
                </Text>
            </Box>
        );
    }

    return (
        <Box className={page}>
            <UnstyledButton className={backLink} onClick={handleBack}>
                <IconArrowLeft size={16} />
                <BackToRemoteAssets />
            </UnstyledButton>

            <Paper p="lg" radius="md" withBorder>
                <div className={header}>
                    <RemoteAssetPreview type={asset.type} previewImageUrl={asset.previewImageUrl} size={40} />
                    <div>
                        <Text className={headerName}>{asset.name}</Text>
                        <Text size="sm" className={typeBadge}>
                            {asset.type}
                        </Text>
                    </div>
                </div>

                <RemoteAssetDetailForm
                    key={asset.id}
                    asset={asset}
                    lenses={lenses}
                    hasMoreLenses={hasMoreLenses}
                    onShowMoreLenses={fetchMoreLenses}
                    loadingMoreLenses={loadingMoreLenses}
                    loadMoreLensesErrorMessage={loadMoreLensesErrorMessage}
                    setMutationStatus={setMutationStatus}
                    isSaving={mutationStatus.type === "pending" && mutationStatus.action === "update"}
                    isDeleting={mutationStatus.type === "pending" && mutationStatus.action === "bulkDelete"}
                    onDeleted={handleBack}
                />
            </Paper>

            {mutationStatus.type === "error" && (
                <Alert
                    color="red"
                    variant="light"
                    mt="sm"
                    withCloseButton
                    onClose={() => {
                        setMutationStatus({ type: "idle" });
                    }}
                >
                    {mutationStatus.message}
                </Alert>
            )}
        </Box>
    );
};
